import { dateHelpers } from '@nocowanie/core/helpers';

import { SORT_VALUES } from '@app/consts/search-filters.consts';
import { AmenityApiMapping } from '@app/enums/amenity-api-mapping.enum';
import { LocationTypeEnum } from '@app/enums/location-type.enum';
import { PropertyTypeApiMapping } from '@app/enums/property-type-api-mapping.enum';
import { AccommodationTilesResponse } from '@app/helpers/accommodation-tiles.helpers';
import { convertFiltersToApiParams } from '@app/helpers/filter-mapping.helpers';
import {
    isIgnoredPropertyTypeCheck,
    isSupportedAmenityCheck,
} from '@app/helpers/listing-mapping.helpers';
import {
    convertReservationFeaturesToBookingOptions,
    mapRatingToApi,
} from '@app/helpers/map-api-params.helpers';
import { AccommodationTag } from '@app/models/accommodation-tag.model';
import { SearchCriteria, SearchFilters } from '@app/models/search.model';
import { cache, cacheKey, cacheTtl } from '@app/services/cache/cache.helpers';
import { getNocApiClientForGuest, sharedNocApiClient } from '@app/services/noc-api/noc-api-client';

import type { ListingAvailableOptions, ListingTile } from '@nocowanie/noc-api';

import type { TFunction } from 'next-i18next';

export const filterUnsupportedFilters = (filters: SearchFilters) => {
    return {
        ...filters,
        amenities: filters.amenities?.filter(amenity => isSupportedAmenityCheck(parseInt(amenity))),
    };
};

export const getParsedListingFilters = ({ searchFilters }: { searchFilters: SearchFilters }) => {
    const {
        propertyType,
        amenities,
        roomEquipment,
        rating,
        provision,
        reservationFeatures,
        sort,
        priceFrom,
        priceTo,
        beachDistance,
        onlineBooking,
    } = searchFilters;

    const parsedOnlineBooking =
        typeof onlineBooking === 'string' ? parseInt(onlineBooking) : undefined;
    const sorting = sort ? SORT_VALUES[sort as keyof typeof SORT_VALUES] : undefined;

    const {
        propertyType: parsedPropertyType,
        amenities: parsedAmenities,
        provision: parsedProvision,
        tags: parsedTags,
    } = convertFiltersToApiParams({
        propertyTypeIds: propertyType
            ?.map(id => parseInt(id))
            .filter(propertyType => !isIgnoredPropertyTypeCheck(propertyType)),
        amenityIds: amenities
            ?.map(id => parseInt(id))
            .filter(amenity => isSupportedAmenityCheck(amenity)),
        provisionIds: provision?.map(id => parseInt(id)),
        roomEquipmentIds: roomEquipment?.map(id => parseInt(id)),
    });

    const parsedReservationFeatures =
        convertReservationFeaturesToBookingOptions(reservationFeatures);

    const amenityGroup = Object.assign({}, parsedAmenities) as any;
    const tags = parsedTags.join(',') as unknown as AccommodationTag[];

    return {
        rating,
        priceFrom,
        priceTo,
        beachDistance,
        sorting,
        onlineBooking: parsedOnlineBooking,
        propertyType: parsedPropertyType,
        provision: parsedProvision,
        reservationFeatures: parsedReservationFeatures,
        amenityGroup,
        tags,
    };
};

export const getListingTiles = async ({
    searchCriteria,
    searchFilters,
    campaignListingId,
    noPrice = false,
    page = 1,
    limit,
    userAgent,
    prettyUrlListing = false,
}: {
    searchCriteria: Partial<SearchCriteria>;
    searchFilters: SearchFilters;
    noPrice?: boolean;
    page?: number;
    campaignListingId?: string;
    limit?: number;
    /** Guest User-Agent, required on server side calls so NOP prices for the right device. */
    userAgent?: string;
    prettyUrlListing?: boolean;
}): Promise<AccommodationTilesResponse | null> => {
    try {
        const {
            checkInDate,
            checkOutDate,
            flexDates,
            poiId,
            geoId,
            locationType,
            guests: { adultsCount, childrenAges, childrenCount } = {},
        } = searchCriteria;

        const {
            rating,
            priceFrom,
            priceTo,
            beachDistance,
            sorting,
            onlineBooking,
            propertyType,
            provision,
            reservationFeatures,
            amenityGroup,
            tags,
        } = getParsedListingFilters({ searchFilters });

        const isCampaignListing = !!campaignListingId;

        const apiClient = getNocApiClientForGuest(userAgent);
        const query = {
            page,
            adults: adultsCount,
            amenity: undefined,
            'amenity-group': amenityGroup,
            'bookable-online':
                onlineBooking === 1 || onlineBooking === 0 ? onlineBooking : undefined,
            'booking-options': Object.assign({}, reservationFeatures) as any,
            category: Object.assign({}, propertyType) as any,
            children: childrenCount,
            'children-age': Object.assign({}, childrenAges),
            'date-from': checkInDate
                ? dateHelpers.format(new Date(checkInDate), 'yyyy-MM-dd')
                : undefined,
            'date-to': checkOutDate
                ? dateHelpers.format(new Date(checkOutDate), 'yyyy-MM-dd')
                : undefined,
            'only-active': undefined,
            provision: Object.assign({}, provision) as any,
            rating: rating ? mapRatingToApi(rating) : undefined,
            region: geoId && locationType === 'region' ? parseInt(geoId) : undefined,
            voivodeship: geoId && locationType === 'voivodeship' ? parseInt(geoId) : undefined,
            latitude: undefined,
            longitude: undefined,
            country: undefined,
            radius: undefined,
            order: sorting
                ? ({
                      [sorting.type]: sorting?.order,
                  } as unknown as string[])
                : undefined,
            city: geoId && locationType === 'city' ? parseInt(geoId) : undefined,
            'noc-country': geoId && locationType === 'country' ? parseInt(geoId) : undefined,
            'flexible-days': flexDates,
            'no-price': noPrice ? 1 : undefined,
            cms: isCampaignListing ? 'campaign-listings' : undefined,
            'cms-field-path': isCampaignListing ? 'accommodationsIDs' : undefined,
            'cms-document-id': isCampaignListing ? campaignListingId : undefined,
            poi: poiId ? `${poiId}` : undefined,
            'price-from': priceFrom ?? undefined,
            'price-to': priceTo ?? undefined,
            beach_distance: beachDistance ?? undefined,
            tags,
            limit,
            'pretty-url-listing': prettyUrlListing ? 1 : undefined,
        };

        const response =
            limit == null && !prettyUrlListing
                ? await apiClient.listingTile.accommodationListingTiles(
                      page,
                      adultsCount,
                      undefined,
                      amenityGroup,
                      onlineBooking === 1 || onlineBooking === 0 ? onlineBooking : undefined,
                      Object.assign({}, reservationFeatures) as any,
                      Object.assign({}, propertyType) as any,
                      childrenCount,
                      Object.assign({}, childrenAges),
                      checkInDate
                          ? dateHelpers.format(new Date(checkInDate), 'yyyy-MM-dd')
                          : undefined,
                      checkOutDate
                          ? dateHelpers.format(new Date(checkOutDate), 'yyyy-MM-dd')
                          : undefined,
                      undefined,
                      Object.assign({}, provision) as any,
                      rating ? mapRatingToApi(rating) : undefined,
                      geoId && locationType === 'region' ? parseInt(geoId) : undefined,
                      geoId && locationType === 'voivodeship' ? parseInt(geoId) : undefined,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      sorting
                          ? ({
                                [sorting.type]: sorting?.order,
                            } as unknown as string[])
                          : undefined,
                      geoId && locationType === 'city' ? parseInt(geoId) : undefined,
                      geoId && locationType === 'country' ? parseInt(geoId) : undefined,
                      flexDates,
                      noPrice ? 1 : undefined,
                      isCampaignListing ? 'campaign-listings' : undefined,
                      isCampaignListing ? 'accommodationsIDs' : undefined,
                      isCampaignListing ? campaignListingId : undefined,
                      poiId ? `${poiId}` : undefined,
                      priceFrom ?? undefined,
                      priceTo ?? undefined,
                      beachDistance ?? undefined,
                      tags,
                  )
                : await apiClient.request.request<{
                      'hydra:member': ListingTile[];
                      'hydra:totalItems'?: number;
                      'hydra:nopTotalItems'?: number;
                      'hydra:realTimeItems'?: number;
                      'hydra:partial'?: boolean;
                  }>({
                      method: 'GET',
                      url: '/api/accommodations/listing/tiles',
                      query,
                      errors: {
                          400: 'Bad request',
                          404: 'Not found',
                          500: 'Internal server error',
                      },
                  });

        return {
            tiles: response['hydra:member'] || [],
            totalItems: response['hydra:totalItems'],
            nopTotalItems: response['hydra:nopTotalItems'],
            realTimeItems: response['hydra:realTimeItems'],
            partial: response['hydra:partial'],
        };
    } catch (error) {
        throw new Error(error instanceof Error ? error.message : 'Failed to fetch listing data');
    }
};

export const getListingAvailableOptions = async (
    locationType: LocationTypeEnum,
    listingId: string,
): Promise<ListingAvailableOptions> => {
    if (locationType === LocationTypeEnum.Accommodation) {
        throw new Error('Available options are not supported for accommodation type!');
    }

    try {
        return await cache(
            cacheKey('listing:available-options-v2', { locationType, listingId }),
            () =>
                sharedNocApiClient.listingAvailableOptions.accommodationListingAvailableOptions(
                    locationType,
                    listingId,
                ),
            cacheTtl('APP_CACHE_LISTING_AVAILABLE_OPTIONS', 604_800), // Cache for 7 days,
        );
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : 'Failed to fetch available options',
        );
    }
};

export const resolveListingHeader = (
    t: TFunction,
    params: {
        locationName: string;
        propertyTypeId?: PropertyTypeApiMapping;
        amenityId?: AmenityApiMapping;
    },
) => {
    const { locationName, propertyTypeId, amenityId } = params;

    if (propertyTypeId && amenityId) {
        return `${locationName} ${t(`propertyTypes.${propertyTypeId}.header`)} ${t(`amenities.${amenityId}.header`)}`;
    }

    if (propertyTypeId) {
        return `${locationName} ${t(`propertyTypes.${propertyTypeId}.header`)}`;
    }

    if (amenityId) {
        return `${locationName} ${t('pageTitleSuffix')} ${t(`amenities.${amenityId}.header`)}`;
    }

    return `${locationName} ${t('pageTitleSuffix')}`;
};
